import {
  IPendingSection2Repository,
  ReassignCompleterPayload,
} from '../../application/ports/outbound/IPendingSection2Repository';
import { PendingSection2Result } from '../../application/domain/entities/PendingSection2Result';
import { SortOrder } from "@/shared/application/domain/enums/SortOrder";
import { PendingSection2RequestDto } from './dto/req/PendingSection2RequestDto';
import { PendingSection2ResponseDto } from './dto/res/PendingSection2ResponseDto';
import { mapPendingSection2ItemDtoToEntity } from './mappers/pendingSection2ItemMapper';
import { HttpClient } from '@/shared/infra/http/HttpClient';
import { I9ListingFilters } from '@/shared/application/domain/entities/I9ListingFilters';

export class ApiPendingSection2Repository implements IPendingSection2Repository {
  constructor(private readonly httpClient: HttpClient) {}

  async fetchPendingSection2Records(
    page: number,
    pageSize: number,
    columnName?: string,
    sortOrder?: SortOrder,
    filters?: I9ListingFilters
  ): Promise<PendingSection2Result> {
    try {
      const request: PendingSection2RequestDto = {
        page,
        pageSize,
        columnName,
        sortOrder,
        ...filters,
      };

      const queryParams = new URLSearchParams({
        page: request.page.toString(),
        pageSize: request.pageSize.toString(),
      });
      if (request.columnName) {
        queryParams.append('columnName', request.columnName);
      }
      if (request.sortOrder) {
        queryParams.append('sortOrder', request.sortOrder);
      }
      // Only non-empty filters are sent: the backend validates `locationId` as a UUID and `hireDate` as
      // a date, so an empty value would be a 400 rather than "no filter".
      if (request.employeeFirstName) {
        queryParams.append('employeeFirstName', request.employeeFirstName);
      }
      if (request.employeeLastName) {
        queryParams.append('employeeLastName', request.employeeLastName);
      }
      if (request.locationId) {
        queryParams.append('locationId', request.locationId);
      }
      if (request.hireDate) {
        queryParams.append('hireDate', request.hireDate);
      }

      const url = `/dashboard/section2/pending?${queryParams.toString()}`;
      const response = await this.httpClient.getPaginated<PendingSection2ResponseDto>(url);

      const pagination = response.pagination || {};
      return {
        items: Array.isArray(response.data)
          ? response.data.map(mapPendingSection2ItemDtoToEntity)
          : [],
        totalPages: pagination.totalPages ?? 1,
        totalItems: pagination.totalItems ?? 0,
        currentPage: pagination.currentPage ?? 1,
      };
    } catch (error) {
      console.error('Error fetching pending section 2 items:', error);
      throw error;
    }
  }

  async deleteRemoteI9(i9FormId: string): Promise<string> {
    try {
      const response = await this.httpClient.delete<string>(`/remote-i9/${i9FormId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting remote I-9:', error);
      throw error;
    }
  }

  async reassignRemoteCompleter(
    i9FormId: string,
    payload: ReassignCompleterPayload
  ): Promise<void> {
    // Errors are rethrown unchanged so the caller can surface the backend's own message — the
    // meaningful failures here (already signed, revoked, completer is the employee) are all cases
    // the admin needs told, not a generic "try again".
    await this.httpClient.patch(`/remote-i9/${i9FormId}/completer`, payload);
  }

  async sendCompleterReviewEmail(i9FormId: string): Promise<void> {
    await this.httpClient.post(`/remote-i9/${i9FormId}/completer-review-email`);
  }
}
